<script lang="ts">
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import Footer from "@retailer/svelte/LimitedOrderingProductRequests/Footer.svelte";
  import TableBody from "@retailer/svelte/LimitedOrderingProductRequests/Pending/TableBody.svelte";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { ProductApprovalsBulkActionSelectAllEntity } from "@shared/services/event_tracking/entities/products/product_approvals_bulk_actions_select_all_entity";
  import { ProductApprovalsBulkActionModalTriggerEntity } from "@shared/services/event_tracking/entities/products/product_approvals_bulk_actions_modal_trigger_entity";
  import { ProductApprovalsBulkActionSendRequestEntity } from "@shared/services/event_tracking/entities/products/product_approvals_bulk_actions_send_request_entity";
  import BulkActionsModal from "@retailer/svelte/LimitedOrderingProductRequests/BulkActionsModal.svelte";
  import ProductApprovalStore from "@shared/svelte/Stores/ProductApprovalStore";

  const hasBulkApprovalRollout = stagedRolloutCheck("adm__bulk_approval");
  const { trackClick } = EventTracking;

  $: selectedProducts = [];
  $: headerCheckboxChecked = selectedProducts.length > 0;

  let showBulkActionsModal = false;
  let modalAction = "Approve";
  let approvalNote = null;

  const closeBulkActionsModal = () => (showBulkActionsModal = false);

  const openBulkActionsModal = action => {
    modalAction = action;
    approvalNote = null;
    showBulkActionsModal = true;

    trackClick(
      "product_approvals_bulk_actions_modal_trigger",
      new ProductApprovalsBulkActionModalTriggerEntity({ selectedProducts, action }),
    );
  };

  const removeFromStore = () => {
    ProductApprovalStore.update(store => {
      store.productRequests = store.productRequests.filter(
        product => !selectedProducts.includes(product.id),
      );
      return store;
    });
  };

  const sendBulkActionsRequest = () => {
    trackClick(
      "product_approvals_bulk_actions_send_request",
      new ProductApprovalsBulkActionSendRequestEntity({ selectedProducts, action: modalAction }),
    );
    HttpPromise({
      method: "PUT",
      url: "/api/retailer/limited_ordering_product_requests/bulk_actions",
      data: {
        limited_ordering_product_request_ids: selectedProducts,
        approval_note: approvalNote,
        request_status: modalAction === "Approve" ? "approved" : "denied",
      },
    }).then(
      function success(res) {
        let message =
          modalAction === "Approve"
            ? "Products approved! It will show up in ordering lists right away."
            : "Products declined";
        closeBulkActionsModal();
        removeFromStore();
        approvalNote = null;

        Flash.now({ type: "success", message, small: true });
      },
      function error(res) {
        Flash.now({
          message: `There was an error trying to ${modalAction.toLocaleLowerCase()} the selected products`,
          type: "error",
        });
      },
    );
  };

  const toggleSelectAll = function () {
    if (selectedProducts.length === 0) {
      selectedProducts = $ProductApprovalStore.productRequests.map(product => product.id);
    } else {
      selectedProducts = [];
    }
  };

  const handleSelectAll = () => {
    toggleSelectAll();
    trackClick(
      "product_approvals_bulk_actions_select_all",
      new ProductApprovalsBulkActionSelectAllEntity({ selectedProducts }),
    );
  };

  const toggleProductCheckbox = request => {
    const limitedOrderingProductId = request.detail;
    const index = selectedProducts.findIndex(productId => productId === limitedOrderingProductId);
    if (index === -1) {
      selectedProducts = [...selectedProducts, limitedOrderingProductId];
    } else {
      selectedProducts = selectedProducts.filter(
        productId => productId !== limitedOrderingProductId,
      );
    }
  };

  const handleClearClick = () => {
    toggleSelectAll();
    trackClick("product_approvals_bulk_actions_clear_all_selected");
  };
</script>

<style>
  th:first-of-type {
    padding-left: 32px;
  }

  th.request-note {
    padding: 1rem 0;
  }

  th.bulk-approve-buttons {
    padding-right: 2rem;
  }

  @media (min-width: 768px) {
    .hide-on-tablet {
      display: none;
    }
  }

  .product-requests-table {
    word-wrap: break-word;
  }
</style>

{#if hasBulkApprovalRollout}
  <div
    class="fizz-flex-row fizz-space-between fizz-inset-square-16 hide-on-tablet fizz-border-bottom"
  >
    {#key headerCheckboxChecked}
      <label class="fizz-flex-row-8 fizz-stack-2" for="header-checkbox">
        <input
          type="checkbox"
          name="checkbox"
          data-testid="mobile-header-checkbox"
          id="header-checkbox"
          class="fizz-checkbox"
          bind:checked={headerCheckboxChecked}
          on:click|preventDefault={handleSelectAll}
        />
        <span>Select All</span>
      </label>
    {/key}
    {#if selectedProducts.length > 0}
      <div>
        <div class="fizz-flex-row">
          <button
            class="fizz-button fizz-button-small"
            on:click={() => openBulkActionsModal("Decline")}
          >
            Decline all
          </button>
          <button
            class="fizz-button-secondary fizz-button-small"
            on:click={() => openBulkActionsModal("Approve")}
          >
            Approve all
          </button>
        </div>
      </div>
    {/if}
  </div>
{/if}

<table
  class="fizz-table fizz-text-medium fizz-expand-16-on-small fizz-table-align-top product-requests-table"
  style="--heading-col-width: 120px"
>
  <thead>
    <tr>
      {#if hasBulkApprovalRollout}
        <th>
          {#key headerCheckboxChecked}
            <label class="fizz-flex-row-8 fizz-stack-2" for="header-checkbox">
              <input
                type="checkbox"
                name="checkbox"
                data-testid="header-checkbox"
                id="header-checkbox"
                class="fizz-checkbox"
                bind:checked={headerCheckboxChecked}
                on:click|preventDefault={handleSelectAll}
              />
            </label>
          {/key}
        </th>
      {/if}
      {#if selectedProducts.length > 0}
        <th class="fizz-flex-row selected-products">
          <span class="fizz-text-strong fizz-inline-0"
            >{selectedProducts.length}
            {pluralize("product", selectedProducts.length)} selected</span
          >
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <span
            class="fizz-link"
            data-testid="clear-product-select-all"
            on:click|preventDefault={handleClearClick}>Clear</span
          >
        </th>
        <th></th>
        <th></th>
        <th></th>
        <th></th>
        <th class="bulk-approve-buttons">
          <div class="fizz-flex-row">
            <button
              class="fizz-button fizz-button-small"
              on:click={() => openBulkActionsModal("Decline")}
            >
              Decline selected requests
            </button>
            <button
              class="fizz-button-secondary fizz-button-small"
              on:click={() => openBulkActionsModal("Approve")}
            >
              Approve selected requests
            </button>
          </div>
        </th>
      {:else}
        <th>Product</th>
        <th>Location</th>
        <th>State</th>
        <th>Requested by</th>
        <th class="date-requested">Date requested</th>
        <th class="request-note" />
        <th class="fizz-align-right" />
      {/if}
    </tr>
  </thead>
  <TableBody
    {selectedProducts}
    {hasBulkApprovalRollout}
    on:openDecisionModal
    on:openRequestNoteModal
    on:toggleProductCheckbox={toggleProductCheckbox}
  />
</table>
<Footer />

<BulkActionsModal
  bind:products={selectedProducts}
  bind:action={modalAction}
  bind:approvalNote={approvalNote}
  show={showBulkActionsModal}
  onClose={closeBulkActionsModal}
  onSuccess={sendBulkActionsRequest}
/>
